import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  DeactivatePromotionPayload,
  PromoSelection,
  Promotion,
  PromotionData,
  PromotionReport,
} from '../../types/promotions.types';
import { useSTKContext } from '@/context/context';

export const usePromotions = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<Promotion[]>>({
    queryKey: ['promotions'],
    queryFn: () => apiClient.get<Promotion[]>('/promotions'),
    enabled,
  });
};

export const usePromotionsData = (enabled: boolean, type: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<PromotionData[]>>({
    queryKey: ['promotionsData'],
    queryFn: () => apiClient.get<PromotionData[]>(`/promotions`, { params: { type } }),
    enabled,
  });
};

export const useCERPromotions = () => {
  const { apiClient } = useSTKContext();
  return useQuery<PromotionData[]>({
    queryKey: ['promotionsData'],
    queryFn: async () => {
      const response = await apiClient.get<PromotionData[]>('/promotions', { params: { type: 'CER' } });
      return response.data;
    },
  });
};

export const usePromotion = (enabled: boolean, promotionId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<Promotion>>({
    queryKey: ['promotion', promotionId],
    queryFn: () => apiClient.get<Promotion>(`/promotions/${promotionId}`),
    enabled,
  });
};
export const useStoresByGroupName = (enabled: boolean, storeGroupName: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<string[]>({
    queryKey: ['stores-by-group-name', storeGroupName],
    queryFn: async () => {
      const response = await apiClient.get<string[]>(`/promotions/stores/${storeGroupName}`);
      return response.data;
    },
    enabled,
  });
};

export const useUpdatePromotionVisibility = (promotionId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ isVisible }: { isVisible: boolean }) =>
      apiClient.put(`/promotions/${promotionId}/visibility`, { isVisible }),
    onSuccess: () => {
      queryClient.invalidateQueries(['promotion', promotionId]);
      queryClient.invalidateQueries(['promotions']);
    },
  });
};

export const useStoreGroupPromotions = (enabled: boolean, groupName: string, type: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<PromotionData[]>({
    queryKey: ['storeGroupPromotions', groupName, type],
    queryFn: async () => {
      if (!groupName || (!groupName && !type)) return [];
      const response = await apiClient.get<PromotionData[]>(`/promotions`, { params: { groupName, type } });
      return response.data;
    },
    enabled,
  });
};

export const useAllPromotionStoreGroups = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<string[]>({
    queryKey: ['all-store-groups'],
    queryFn: async () => {
      const response = await apiClient.get<string[]>(`/promotions/storeGroups`);
      return response.data;
    },
    enabled,
  });
};

export const usePromoLockUpdate = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: PromoSelection[]) => apiClient.put('/promotions/lock', requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries(['promotionsData']);
    },
  });
};

export const usePromotionsDeactivate = (promotionId?: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeactivatePromotionPayload) => apiClient.put('/promotions/assignment', payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['promotion', promotionId]);
    },
  });
};

export const useUpdateCERWindow = () => {
  const { apiClient } = useSTKContext();
  return useMutation({
    mutationFn: (windowType: string) => apiClient.put(`/promotions/cer-update/${windowType}`),
  });
};

export const usePromotionsReport = () => {
  const { apiClient } = useSTKContext();
  return useQuery<PromotionReport[]>({
    queryKey: ['promotionReport'],
    queryFn: async () => {
      const response = await apiClient.get<PromotionReport[]>('/promotions/cer-opt-in-out-report');
      return response.data;
    },
  });
};
